import { type QueryClient } from "@tanstack/react-query";
import { type AxiosInstance } from "axios";
import { fetchSite } from "./useSites.ts";
import {
  extractSiteGeometryId,
  fetchSiteGeometry,
  siteGeometryQueryKey,
} from "./useSiteGeometry.ts";

export function writeGeometryQueryCache(
  queryClient: QueryClient,
  savedGeometry: Record<string, unknown>
) {
  const geometryKey = siteGeometryQueryKey(
    (savedGeometry.url as string | undefined) ??
      (savedGeometry.id as string | number)
  );
  queryClient.setQueryData(geometryKey, { data: savedGeometry });

  const geometryId =
    savedGeometry.id != null
      ? savedGeometry.id
      : extractSiteGeometryId(savedGeometry.url as string | undefined);
  if (geometryId != null) {
    queryClient.removeQueries({
      queryKey: ["site_geometry_geojson", geometryId],
    });
  }
}

/** Update site cache with geometry link; creates cache entry when missing. */
export function patchSiteGeometryLink(
  queryClient: QueryClient,
  siteId: string | number,
  geometryUrl: string
) {
  queryClient.setQueryData(
    ["site", String(siteId)],
    (old: { data?: Record<string, unknown> } | undefined) => ({
      ...(old ?? {}),
      data: {
        ...(old?.data ?? {}),
        sitegeometry: geometryUrl,
        sitegeometry_synthetic: false,
      },
    })
  );
}

export function writeSiteQueryCache(
  queryClient: QueryClient,
  siteId: string | number,
  site: Record<string, unknown>,
  sitegeometrySynthetic?: boolean
) {
  queryClient.setQueryData(["site", String(siteId)], {
    data: {
      ...site,
      ...(sitegeometrySynthetic != null
        ? { sitegeometry_synthetic: sitegeometrySynthetic }
        : {}),
    },
  });
}

/** Force-fetch site + geometry from the API and write into React Query cache. */
export async function refreshSiteAndGeometryCaches(
  axiosAuth: AxiosInstance,
  queryClient: QueryClient,
  siteId: string | number,
  previousGeometryId?: string | number | null
) {
  const normalizedSiteId = String(siteId);

  if (previousGeometryId != null) {
    queryClient.removeQueries({
      queryKey: siteGeometryQueryKey(previousGeometryId),
    });
  }

  const cachedSite = queryClient.getQueryData<{ data?: { sitegeometry?: string } }>([
    "site",
    normalizedSiteId,
  ]);
  const cachedGeometryUrl = cachedSite?.data?.sitegeometry;

  const siteResponse = await fetchSite(axiosAuth, normalizedSiteId);
  const fetchedGeometryUrl = siteResponse.data?.sitegeometry as string | undefined;

  if (
    cachedGeometryUrl &&
    fetchedGeometryUrl &&
    cachedGeometryUrl !== fetchedGeometryUrl
  ) {
    queryClient.removeQueries({
      queryKey: siteGeometryQueryKey(cachedGeometryUrl),
    });
  }
  queryClient.setQueryData(["site", normalizedSiteId], siteResponse);

  const geometryUrl = siteResponse.data?.sitegeometry as string | undefined;
  if (!geometryUrl) {
    return { site: siteResponse.data, geometry: null };
  }

  const geometryResponse = await fetchSiteGeometry(axiosAuth, geometryUrl);
  queryClient.setQueryData(siteGeometryQueryKey(geometryUrl), geometryResponse);

  const geometryId = extractSiteGeometryId(geometryUrl);
  if (geometryId != null) {
    queryClient.removeQueries({
      queryKey: ["site_geometry_geojson", geometryId],
    });
  }

  return {
    site: siteResponse.data,
    geometry: geometryResponse.data,
  };
}
